import { useState, useRef, ElementRef, useCallback, useEffect } from 'react';

import { Box, Input, Loader } from '@bonanza-social/moonlight';

import { OTCInputProps } from './OTCInput.types';
import OTCInputCell from './OTCInputCell';
import { TextInput } from 'react-native';

function OTCInput(props: OTCInputProps) {
  const {
    autoFocus = true,
    blurOnSubmit = true,
    codeLength,
    onChange,
    onFulfill,
    value,
    controllerRef,
    disabled = false,
    loading,
  } = props;
  const [cellsArray] = useState([...new Array(codeLength)]);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<ElementRef<typeof Input>>(null);

  const onValueChange = useCallback(
    (val: string) => {
      const validatedValue = val.replace(/\D/g, '');
      onChange(validatedValue);

      if (validatedValue.length > codeLength) {
        onFulfill(val);

        if (blurOnSubmit) {
          inputRef.current?.blur();
        }
      }
    },
    [onChange, blurOnSubmit, codeLength, onFulfill],
  );

  const onCellPress = useCallback(
    (index: number) => {
      if (!disabled) {
        onValueChange(value.substring(0, index));

        inputRef.current?.focus();
      }
    },
    [onValueChange, value, inputRef, disabled],
  );

  const onInputPress = useCallback(() => {
    onValueChange('');
  }, [onValueChange]);

  const handleSetFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);

  const handleSetBlur = useCallback(() => {
    setIsFocused(false);
  }, [setIsFocused]);

  useEffect(() => {
    if (value.length === codeLength) {
      onFulfill(value);
    }
  }, [value, onFulfill, codeLength]);

  useEffect(() => {
    if (controllerRef && 'current' in controllerRef) {
      controllerRef.current = {
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
      };
    }
  }, [controllerRef]);

  return (
    <Box justifyContent="center" alignItems="center">
      <Box justifyContent="center" alignItems="center" flexDirection="row">
        <Box position="relative" flexDirection="row" gap="space-100">
          {cellsArray.map((_, index) => (
            <OTCInputCell
              isActive={isFocused && index === value.length}
              key={index}
              onPress={() => onCellPress(index)}
              value={value[index]}
            />
          ))}
          {loading ? (
            <Box
              position="absolute"
              top="space-0"
              left="space-0"
              width="100%"
              height="100%"
              justifyContent="center"
              backgroundColor="accentBackground"
              alignItems="center"
            >
              <Loader />
            </Box>
          ) : null}
          <TextInput
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.1,
            }}
            autoComplete="sms-otp"
            autoFocus={autoFocus}
            importantForAutofill="yes"
            keyboardType="number-pad"
            maxLength={codeLength}
            onBlur={handleSetBlur}
            onChangeText={onValueChange}
            onFocus={handleSetFocus}
            ref={inputRef}
            textContentType="oneTimeCode"
            onPressIn={onInputPress}
            value={value}
            testID="one-time-code-screen-input"
          />
        </Box>
      </Box>
    </Box>
  );
}

export default OTCInput;
